import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const DermalFillers = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const benefits = [
    {
      title: 'Immediate Volume Enhancement',
      description: 'Dermal fillers provide instant results, adding volume to facial areas that have lost fullness due to aging or genetic factors.'
    },
    {
      title: 'Smooths Wrinkles and Fine Lines',
      description: 'Effectively reduces the appearance of wrinkles, creases, and fine lines for a smoother, more youthful complexion.'
    },
    {
      title: 'Natural-Looking Results',
      description: 'When administered by experienced professionals, dermal fillers provide subtle, natural-looking enhancements.'
    },
    {
      title: 'Restores Facial Symmetry',
      description: 'Can be used to balance facial features and restore proper proportions for improved facial harmony.'
    },
    {
      title: 'Long-Lasting Effects',
      description: 'Results typically last 6-18 months depending on the type of filler used and treatment area.'
    },
    {
      title: 'Minimal Downtime',
      description: 'Non-surgical procedure with minimal recovery time, allowing you to return to daily activities quickly.'
    },
    {
      title: 'Customizable Treatments',
      description: 'Can be tailored to address specific concerns in various facial areas including lips, cheeks, and under-eye hollows.'
    },
    {
      title: 'Safe and FDA-Approved',
      description: 'Uses biocompatible materials that are safe and approved for cosmetic use with a proven track record.'
    },
    {
      title: 'Boosts Self-Confidence',
      description: 'Enhances facial appearance and helps patients feel more confident about their looks.'
    },
    {
      title: 'Reversible if Needed',
      description: 'Hyaluronic acid fillers can be dissolved if desired, offering flexibility and peace of mind.'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-28">
      

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/dermal-fillers.jpg" 
                  alt="Dermal Fillers Treatment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                DERMAL FILLERS TO ENHANCE FACIAL SYMMETRY
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Dermal fillers are injectable treatments designed to restore facial volume, smooth wrinkles, and enhance contours for a youthful appearance. These cosmetic injectables use hyaluronic acid and other biocompatible materials to replenish lost collagen and elasticity, addressing common signs of aging like sagging skin, fine lines, and volume loss in the cheeks, lips, and jawline.
                </p>
                <p>
                  At Century Medical Services in Forest Hills, NY, we offer Evolyse dermal fillers—an advanced hyaluronic acid-based treatment that provides natural-looking results with minimal downtime. Evolyse is FDA-approved and specifically formulated to restore facial symmetry, smooth deep folds, and enhance lip fullness. The quick 15-30 minute procedure delivers immediate, long-lasting results that typically last 6-18 months. Whether you're looking to restore lost volume, reduce wrinkles, or achieve balanced facial features, Evolyse dermal fillers offer a safe, effective, non-surgical solution. Book your consultation today to discover if dermal filler treatment is right for you.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Dermal Fillers Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-md mx-auto bg-white rounded-lg p-12 shadow-2xl">
            <div className="text-center">
              {/* Elegant Icon */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
                <svg 
                  className="w-12 h-12 text-gold" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                  />
                </svg>
              </div>
              
              <h3 className="text-2xl font-heading font-normal text-olive-darkest mb-3 tracking-wider">
                Evolyse
              </h3>
              <p className="text-6xl font-heading font-light text-gold mb-2 tracking-tight">$599</p>
              <p className="text-lg font-body text-gray-600 mb-8">per syringe</p>
              
              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full bg-olive-darkest text-white px-8 py-4 rounded-lg font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </div>
          </div>

          {/* Free Consultation Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 bg-gold/10 border-2 border-gold px-8 py-4 rounded-lg">
              <CheckCircle className="w-6 h-6 text-gold" />
              <p className="text-white text-lg font-medium">
                Free Dermal Fillers Consultation
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Dermal Fillers</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          {/* Carousel Container */}
          <div className="relative max-w-5xl mx-auto px-16">
            {/* Navigation Buttons - Positioned Outside */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentSlide((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-olive-darkest text-white p-3 rounded-full shadow-lg hover:bg-gold hover:text-olive-darkest transition-all duration-300 z-10"
              aria-label="Previous benefit"
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentSlide((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-olive-darkest text-white p-3 rounded-full shadow-lg hover:bg-gold hover:text-olive-darkest transition-all duration-300 z-10"
              aria-label="Next benefit"
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>

            {/* Carousel Items */}
            <div className="overflow-hidden">
              <motion.div
                className="flex"
                animate={{
                  x: `-${currentSlide * 100}%`
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30
                }}
              >
                {benefits.map((benefit, index) => (
                  <div key={benefit.title} className="min-w-full px-4">
                    <BenefitCard benefit={benefit} index={index} />
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {benefits.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    currentSlide === index
                      ? 'w-8 h-3 bg-olive-darkest'
                      : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Why Choose Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Why Choose Century Medical Services for Dermal Fillers?</h2>
              <div className="w-20 h-1 bg-gold mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  title: 'Expert Injectors',
                  description: 'Our medical professionals have extensive training and experience in facial anatomy and dermal filler techniques.'
                },
                {
                  title: 'Premium Products',
                  description: 'We use only FDA-approved, high-quality dermal fillers from trusted manufacturers for safe, effective results.'
                },
                {
                  title: 'Personalized Approach',
                  description: 'Each treatment is customized to your unique facial structure, aesthetic goals, and desired outcomes.'
                },
                {
                  title: 'Natural Results',
                  description: 'We focus on subtle enhancements that look natural and harmonious with your facial features.'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex gap-4 items-start"
                >
                  <CheckCircle className="w-6 h-6 text-olive-darkest flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-heading font-bold text-olive-darkest mb-2 text-lg">{item.title}</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-10 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
            >
              <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
                Experience the transformative power of dermal fillers. Restore volume, smooth wrinkles, and enhance your natural beauty with our expert treatments!
              </p>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
              >
                Schedule Consultation
              </motion.button>

              {/* Divider */}
              <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

              {/* Have Questions Section */}
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
              <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
                Learn about dermal filler treatments, what to expect during your appointment, and how we can help you achieve your aesthetic goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <motion.button
                  whileHover={{ y: -2, scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('dermal-fillers');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                  className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
                >
                  View FAQs
                </motion.button>
                <motion.button
                  whileHover={{ y: -2, scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
                >
                  Contact Us Directly
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// BenefitCard Component
const BenefitCard = ({ benefit, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.05 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative bg-white rounded-lg shadow-md overflow-hidden cursor-pointer group"
    >
      {/* Background color overlay */}
      <motion.div
        initial={{ height: 0 }}
        animate={{ height: isHovered ? '100%' : 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        className="absolute bottom-0 left-0 w-full bg-olive-darkest z-0"
      />

      {/* Content */}
      <div className="relative z-10 p-6">
        <motion.div
          animate={{ 
            color: isHovered ? '#B8956A' : '#4A4D3A'
          }}
          transition={{ duration: 0.4 }}
          className="mb-4"
        >
          <CheckCircle className="w-12 h-12" strokeWidth={1.5} />
        </motion.div>

        <motion.h3
          animate={{ 
            color: isHovered ? '#FFFFFF' : '#4A4D3A'
          }}
          transition={{ duration: 0.4 }}
          className="font-heading text-2xl font-semibold mb-3"
        >
          {benefit.title}
        </motion.h3>

        <motion.p
          animate={{ 
            color: isHovered ? '#FFFFFF' : '#374151',
            opacity: isHovered ? 0.95 : 0.8
          }}
          transition={{ duration: 0.4 }}
          className="text-sm leading-relaxed"
        >
          {benefit.description}
        </motion.p>
      </div>

      {/* Border effect */}
      <motion.div
        animate={{ 
          borderTopColor: isHovered ? '#B8956A' : 'transparent'
        }}
        transition={{ duration: 0.4 }}
        className="absolute top-0 left-0 w-full h-1 border-t-4"
      />
    </motion.div>
  );
};

export default DermalFillers;